import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import client, { formatApiError } from "@/lib/api";
import {
  Plus, Upload, Pencil, CheckCircle2, Circle, BookOpen,
  Star, Cloud, Flower2, Waves, Moon, Sun, Feather, Sparkles, Bird, Fish,
} from "lucide-react";
import AdminPageHeader from "@/components/admin/AdminPageHeader";
import { AdminFilterBar, AdminSearchInput } from "@/components/admin/AdminFilterBar";
import AdminButton from "@/components/admin/AdminButton";
import AdminModal from "@/components/admin/AdminModal";
import AdminEmptyState from "@/components/admin/AdminEmptyState";

// Card-grid layout borrowed from the Little Verses admin, colored with 600
// Stories' own palette — soft tints of hv-teal/coral/gold/navy — instead of
// poems' green theme.
const CARD_BG = ["bg-hv-tealTint", "bg-hv-coralTint", "bg-hv-goldTint", "bg-hv-navyTint"];
const CARD_ICONS = [Star, Cloud, Flower2, Waves, Moon, Sun, Feather, Sparkles, Bird, Fish];

export default function AdminStories() {
  const navigate = useNavigate();
  const [stories, setStories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");
  const [err, setErr] = useState("");
  const [msg, setMsg] = useState("");
  const [bulkJson, setBulkJson] = useState("");
  const [bulkOpen, setBulkOpen] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await client.get("/stories?limit=600");
      setStories(data.stories);
    } catch (ex) {
      setErr(formatApiError(ex.response?.data?.detail) || ex.message);
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); }, []);

  const filtered = useMemo(() => {
    const needle = q.trim().toLowerCase();
    if (!needle) return stories;
    return stories.filter((s) =>
      String(s.global_sequence).includes(needle) ||
      String(s.juz_number).includes(needle) ||
      (s.title?.en || "").toLowerCase().includes(needle)
    );
  }, [stories, q]);

  const nextSeq = useMemo(() => {
    const max = stories.reduce((m, s) => Math.max(m, s.global_sequence || 0), 0);
    return Math.min(max + 1, 600);
  }, [stories]);

  const bulkImport = async () => {
    setErr(""); setMsg("");
    try {
      const parsed = JSON.parse(bulkJson);
      const importStories = Array.isArray(parsed) ? parsed : parsed.stories;
      const { data } = await client.post("/stories/bulk-import", { stories: importStories });
      setMsg(`Bulk import: ${data.upserted} stories upserted.`);
      setBulkJson("");
      setBulkOpen(false);
      load();
    } catch (ex) {
      setErr(ex.response?.data ? formatApiError(ex.response.data.detail) : (ex.message || "Invalid JSON"));
    }
  };

  return (
    <div data-testid="admin-stories">
      <AdminPageHeader
        title="Stories library"
        subtitle={`${stories.length} of 600 stories · fill in each one's media, storybook, quiz, and translations`}
        actions={
          <>
            <AdminButton variant="secondary" icon={Upload} onClick={() => setBulkOpen(true)} data-testid="admin-stories-bulk-toggle">
              Bulk import
            </AdminButton>
            <AdminButton variant="primary" icon={Plus} data-testid="admin-stories-new"
                         onClick={() => navigate("/admin/stories/new", { state: { suggestedSeq: nextSeq } })}>
              New story
            </AdminButton>
          </>
        }
      />

      <AdminFilterBar>
        <AdminSearchInput value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search by title, sequence, or juz…" testId="admin-stories-search" />
      </AdminFilterBar>

      {err && <div className="mt-4 text-hv-coral font-bold text-sm" data-testid="admin-stories-error">{err}</div>}
      {msg && <div className="mt-4 text-hv-teal font-bold text-sm" data-testid="admin-stories-msg">{msg}</div>}

      <AdminModal open={bulkOpen} onClose={() => setBulkOpen(false)} title="Bulk import" maxWidth="max-w-2xl" testId="admin-stories-bulk">
        <p className="text-hv-slate text-sm">Paste a JSON array (or {"{ stories: [...] }"}) of story objects. Upserts by global_sequence.</p>
        <textarea value={bulkJson} onChange={(e) => setBulkJson(e.target.value)} rows={8}
                  data-testid="admin-stories-bulk-textarea"
                  className="w-full px-3 py-2 rounded-xl border border-[#E5DFC8] font-mono text-xs" />
        <div className="flex justify-end gap-2 pt-2">
          <AdminButton variant="secondary" onClick={() => setBulkOpen(false)}>Cancel</AdminButton>
          <AdminButton variant="primary" onClick={bulkImport} data-testid="admin-stories-bulk-submit">Import</AdminButton>
        </div>
      </AdminModal>

      {loading ? (
        <div className="mt-8 text-hv-slate">Loading…</div>
      ) : (
        <div className="mt-6 grid grid-cols-1 sm:grid-cols-2 gap-5" data-testid="admin-stories-list">
          {filtered.map((s, idx) => {
            const bg = CARD_BG[idx % CARD_BG.length];
            const Icon = CARD_ICONS[s.global_sequence % CARD_ICONS.length];
            const isFree = s.global_sequence <= 2;
            const hasContent = !!(s.storybook?.length && s.quiz?.length);
            return (
              <div key={s.global_sequence}
                   className={`text-left rounded-[2rem] p-6 ${bg} text-hv-navy shadow-md shadow-black/5 transition-transform hover:-translate-y-1`}
                   data-testid={`admin-story-card-${s.global_sequence}`}>
                <div className="flex items-start justify-between">
                  <span className="rounded-full bg-white/70 px-3 py-1 text-xs font-bold uppercase tracking-wider">
                    #{String(s.global_sequence).padStart(2, "0")}
                  </span>
                  <div className="rounded-2xl bg-white/60 p-2">
                    <Icon className="w-6 h-6" strokeWidth={2.4} />
                  </div>
                </div>

                <h3 className="hv-heading text-2xl mt-6 leading-tight">{s.title?.en || "(untitled)"}</h3>

                <div className="mt-3 flex items-center gap-2 text-sm font-semibold flex-wrap">
                  {isFree && <span className="rounded-full bg-hv-coral/85 text-white px-2 py-0.5">Free</span>}
                  <span className="rounded-full bg-white/60 px-2 py-0.5 inline-flex items-center gap-1">
                    {hasContent ? <CheckCircle2 size={13} strokeWidth={2.8} /> : <Circle size={13} strokeWidth={2.8} />}
                    {hasContent ? "Content ready" : "Needs content"}
                  </span>
                  <span className="rounded-full bg-white/60 px-2 py-0.5">Juz {s.juz_number}</span>
                  <span className="rounded-full bg-white/60 px-2 py-0.5">{(s.quiz?.length ?? 0)} quiz</span>
                </div>

                <button onClick={() => navigate(`/admin/stories/${s.global_sequence}`)}
                        data-testid={`admin-stories-edit-${s.global_sequence}`}
                        className="mt-4 inline-flex items-center gap-1 text-sm font-bold underline">
                  <Pencil className="w-3.5 h-3.5" strokeWidth={3} /> Edit
                </button>
              </div>
            );
          })}
          {filtered.length === 0 && (
            <div className="col-span-full hv-card p-10 text-center text-hv-slate">
              No stories match.
            </div>
          )}
        </div>
      )}
    </div>
  );
}
